'use client';

import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useRouter } from 'next/navigation';
import { AnimatePresence, motion } from 'framer-motion';

const CALENDLY_URL = 'https://calendly.com/creatortech-info/new-meeting';
const THANK_YOU_PATH = '/thank-you';

type CalendlyContextType = { open: () => void };
const CalendlyContext = createContext<CalendlyContextType>({ open: () => {} });

export function useCalendly() {
  return useContext(CalendlyContext);
}

function CalendlyModal({ visible, onClose }: { visible: boolean; onClose: () => void }) {
  const router = useRouter();

  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);

    const onMessage = (event: MessageEvent) => {
      if (
        event.data?.event === 'calendly.event_scheduled' ||
        (typeof event.data === 'string' && event.data.includes('calendly') && event.data.includes('scheduled'))
      ) {
        const name =
          event.data?.payload?.invitee?.name || '';
        onClose();
        const params = name ? `?invitee_name=${encodeURIComponent(name)}` : '';
        router.push(`${THANK_YOU_PATH}${params}`);
      }
    };
    window.addEventListener('message', onMessage);

    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('message', onMessage);
    };
  }, [visible, onClose, router]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <motion.div
            className="absolute inset-0 bg-[#0F1117]/60 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          <motion.div
            className="relative z-10 w-full max-w-[680px] overflow-hidden rounded-2xl bg-white shadow-[0_40px_100px_-40px_rgba(15,17,23,0.55)]"
            style={{ height: 'min(88vh, 720px)' }}
            initial={{ opacity: 0, scale: 0.95, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <button
              onClick={onClose}
              className="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-[#0F1117]/[0.06] text-[#0F1117]/60 transition-colors hover:bg-[#0F1117]/[0.12] hover:text-[#0F1117]"
              aria-label="Close booking"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <iframe
              src={CALENDLY_URL}
              className="h-full w-full border-0"
              title="Book a Free Consultation"
              loading="lazy"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export const CalendlyProvider = memo(function CalendlyProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [visible, setVisible] = useState(false);
  const open = useCallback(() => setVisible(true), []);
  const close = useCallback(() => setVisible(false), []);

  return (
    <CalendlyContext.Provider value={{ open }}>
      {children}
      <CalendlyModal visible={visible} onClose={close} />
    </CalendlyContext.Provider>
  );
});
